import { Button, Text } from "@aurem/ui";
import { Frown } from "lucide-react";
import { useI18n } from "providers/I18nProvider";
import { ChatErrorType } from "types/graphql/graphql";

interface ChatErrorDisplayProps {
  errorMetadata?: {
    type: ChatErrorType;
    error: string | null;
  } | null;
  retryCount?: number | null;
  onRetry?: () => void;
}

const MAX_RETRIES = 3;

export function ChatErrorDisplay({
  errorMetadata,
  retryCount = 0,
  onRetry,
}: ChatErrorDisplayProps) {
  const { t } = useI18n();

  const getTranslation = (type: ChatErrorType): string => {
    switch (type) {
      case ChatErrorType.ExecutionLimit:
        return t(`apps.admin.chatAI.messages.errors.executionLimit`);
      case ChatErrorType.LlmValidation:
        return t(`apps.admin.chatAI.messages.errors.llmValidation`);
      case ChatErrorType.WorkflowState:
        return t(`apps.admin.chatAI.messages.errors.workflowState`);
      case ChatErrorType.DataProcessing:
        return t(`apps.admin.chatAI.messages.errors.dataProcessing`);
      case ChatErrorType.Timeout:
        return t(`apps.admin.chatAI.messages.errors.timeout`);
      case ChatErrorType.RateLimit:
        return t(`apps.admin.chatAI.messages.errors.rateLimit`);
      default:
        return t(`apps.admin.chatAI.messages.errors.unexpected`);
    }
  };

  const getErrorMessage = (): string => {
    return getTranslation(errorMetadata?.type ?? ChatErrorType.Unknown);
  };

  const errorKey = getErrorMessage();
  const canRetry =
    errorMetadata?.type !== ChatErrorType.Unknown &&
    (retryCount ?? 0) < MAX_RETRIES;

  return (
    <div className="space-y-4">
      <div className="flex items-start gap-2 px-3">
        <Frown className="w-4 h-4 text-danger-800 mt-0.5 flex-shrink-0" />
        <Text size="sm" weight="medium" className="text-danger-800">
          {errorKey}
          {canRetry && (
            <> {t("apps.admin.chatAI.messages.errors.retryPrompt")}</>
          )}
        </Text>
      </div>
      {canRetry && onRetry && (
        <div className="flex gap-2 px-3">
          {onRetry && (
            <Button
              size="sm"
              color="primary"
              variant="outlined"
              className="rounded-xl"
              onClick={onRetry}
            >
              {t("apps.admin.chatAI.messages.errors.retryCta")}{" "}
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
